// UPPDRAG-62 PR-D: Centralized "is the given user on this team?" lookup.
//
// Background — the legacy filter pattern
//   `.or("player1_id.eq.<uid>,player2_id.eq.<uid>")`
// was scattered across 11 frontend files. It silently missed registered
// roster members who joined via UPPDRAG-60's customer_id flow — they
// live in `team_members.user_id`, not `teams.player1/2`.
//
// TASK-66 PR-D: was previously a 3-source union including captain_id.
// captain_id stripped — pre-TASK-66 captains still arrive via the
// team_members membership branch (every captain has a team_members row).
//
// TASK-67 PR-D: dropped the teams.player1/2_id branch entirely. PR-A's
// backfill (migration 20260504074158) ensured every team has a
// team_members row for both player1_id and player2_id, so the
// team_members branch alone now returns the same set the union
// previously returned. Verified pre-migration via PR-A's 10/10 metrics
// (0 player1 orphans, 0 player2 orphans).
//
// `fetchTeamsForUser` now returns the team_members branch ONLY:
//   - team_members WHERE user_id = userId  (joined via team_id)
//
// Optional `leagueId`, `status`, `limit` filters apply to the embedded
// teams row via post-fetch filter (PostgREST can't filter the embedded
// teams row inside the team_members query). n per user is tiny so this
// is free.
//
// Used by:
//   - useMyTeamForLeague hook (current-user, single league)
//   - direct calls from admin customer-detail views (other user)
//   - direct calls from list pages (current-user, all leagues)

import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

export interface FindTeamsForUserOptions {
  /** Optional league_id filter (matches teams.league_id). */
  leagueId?: string | null;
  /** Optional teams.status filter (e.g. "active"). */
  status?: string;
  /**
   * Columns spec passed to PostgREST `.select()`. Default `"*"`.
   * Embeds (e.g. `"leagues(name)"`) are applied to the embedded
   * `team:teams!inner(...)` inside team_members.
   */
  columns?: string;
  /**
   * Optional row LIMIT applied to the team_members branch.
   */
  limit?: number;
}

/**
 * Minimum row shape returned by `fetchTeamsForUser`. Real callers can
 * pass a wider TRow generic that includes any embeds requested via the
 * `columns` option.
 */
export interface TeamMembershipRow {
  id: string;
  league_id?: string | null;
  status?: string | null;
}

/**
 * Returns the array of teams (deduped by id) where `userId` is a
 * participant via team_members. See file header for the historical
 * union semantics — TASK-67 PR-D collapsed it to the team_members
 * branch alone.
 *
 * Throws on the query erroring (matches the existing `if (error) throw`
 * pattern used by the consumer call sites).
 */
export async function fetchTeamsForUser<TRow extends TeamMembershipRow = TeamMembershipRow>(
  supabase: SupabaseClient<Database>,
  userId: string,
  options: FindTeamsForUserOptions = {},
): Promise<TRow[]> {
  const columns = options.columns ?? "*";

  // TASK-67 PR-D: team_members membership is now the SOLE source.
  // PR-A's backfill ensured every player1/2 user_id has a team_members
  // row, so the previous teams.player1/2 branch is redundant.
  // !inner ensures we drop team_members rows with a deleted team FK.
  let membersQuery = supabase
    .from("team_members")
    .select(`team:teams!inner(${columns})`)
    .eq("user_id", userId);
  if (options.limit) membersQuery = membersQuery.limit(options.limit);

  const membersRes = await membersQuery;
  if (membersRes.error) throw membersRes.error;

  const map = new Map<string, TRow>();
  for (const m of (membersRes.data as unknown as { team: TRow | null }[] | null) ?? []) {
    const t = m?.team;
    if (!t?.id || map.has(t.id)) continue;
    // Apply post-fetch filters (PostgREST embed doesn't accept these via
    // query syntax).
    if (options.leagueId && t.league_id !== options.leagueId) continue;
    if (options.status && t.status !== options.status) continue;
    map.set(t.id, t);
  }

  const out = Array.from(map.values());
  if (options.limit && out.length > options.limit) {
    return out.slice(0, options.limit);
  }
  return out;
}

/**
 * Convenience: returns the array of team IDs where `userId` is a
 * participant. Cheaper than `fetchTeamsForUser` when the caller only
 * needs the membership set (e.g. the LeagueListPanel filter).
 */
export async function fetchTeamIdsForUser(
  supabase: SupabaseClient<Database>,
  userId: string,
  options: FindTeamsForUserOptions = {},
): Promise<string[]> {
  // We need league_id and status if the caller passed those filters,
  // otherwise the post-fetch filter loop in fetchTeamsForUser would
  // misfire. Append them to the columns spec defensively.
  const baseColumns = options.columns ?? "id";
  const needsLeague = options.leagueId && !baseColumns.includes("league_id");
  const needsStatus = options.status && !baseColumns.includes("status");
  const columns = [
    baseColumns,
    needsLeague ? "league_id" : null,
    needsStatus ? "status" : null,
  ]
    .filter(Boolean)
    .join(", ");
  const teams = await fetchTeamsForUser<TeamMembershipRow>(supabase, userId, {
    ...options,
    columns,
  });
  return teams.map((t) => t.id);
}
